import React from 'react';
import './RefundPolicy.css';
import SEOHead from '../SEOHead';

const RefundPolicy: React.FC = () => {
  // Immediate scroll to top on component render
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="refund-policy">
      <SEOHead
        title="Refund & Cancellation Policy"
        description="Learn about Antaryatra's refund and cancellation policy for puja bookings. Full refunds available 24 hours before the ceremony."
        canonicalPath="/refund-policy"
        noIndex={false}
      />
      <div className="container">
        <div className="legal-content">
          <h1>Refund Policy</h1>
          <div className="last-updated">
            Last updated: January 10, 2026
          </div>

          <section>
            <h2>1. Overview</h2>
            <p>
              This Refund Policy outlines the conditions under which refunds may be processed for 
              services booked through Antaryatra. We strive to provide satisfactory services while 
              maintaining fair policies for all parties involved.
            </p>
          </section>

          <section>
            <h2>2. Refund Eligibility</h2>
            <p>Refunds may be considered in the following circumstances:</p>
            <ul>
              <li>Service provider fails to show up for the scheduled service</li>
              <li>Service is significantly different from what was advertised</li>
              <li>Cancellation made within the specified timeframe (see cancellation policy)</li>
              <li>Technical issues on our platform prevent service delivery</li>
              <li>Service provider cancels the service</li>
            </ul>
          </section>

          <section>
            <h2>3. Cancellation Timeframes</h2>
            <div className="policy-table">
              <h3>Standard Services</h3>
              <ul>
                <li><strong>More than 48 hours before service:</strong> Full refund (100%)</li>
                <li><strong>24-48 hours before service:</strong> Partial refund (75%)</li>
                <li><strong>12-24 hours before service:</strong> Partial refund (50%)</li>
                <li><strong>Less than 12 hours before service:</strong> No refund</li>
              </ul>

              <h3>Premium Services</h3>
              <ul>
                <li><strong>More than 72 hours before service:</strong> Full refund (100%)</li>
                <li><strong>48-72 hours before service:</strong> Partial refund (75%)</li>
                <li><strong>24-48 hours before service:</strong> Partial refund (50%)</li>
                <li><strong>Less than 24 hours before service:</strong> No refund</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>4. Non-Refundable Situations</h2>
            <p>Refunds will not be processed in the following cases:</p>
            <ul>
              <li>Services have been completed as agreed</li>
              <li>Cancellation made outside the specified timeframes</li>
              <li>User fails to be present for the scheduled service</li>
              <li>Changes in personal circumstances or preferences</li>
              <li>Dissatisfaction with service quality (may be eligible for partial refund review)</li>
              <li>Services partially consumed</li>
            </ul>
          </section>

          <section>
            <h2>5. Refund Process</h2>
            <p>To request a refund:</p>
            <ol>
              <li>Contact our customer support within 24 hours of the issue</li>
              <li>Provide your booking reference number and reason for refund</li>
              <li>Submit any supporting documentation if required</li>
              <li>Our team will review your request within 2-3 business days</li>
              <li>If approved, refunds will be processed within 5-7 business days</li>
            </ol>
          </section>

          <section>
            <h2>6. Refund Methods</h2>
            <p>Refunds will be processed using the following methods:</p>
            <ul>
              <li><strong>Credit/Debit Card:</strong> Refunded to the original payment method</li>
              <li><strong>Digital Wallets:</strong> Refunded to the original wallet</li>
              <li><strong>Bank Transfer:</strong> Direct deposit to your bank account</li>
              <li><strong>Platform Credits:</strong> Credits added to your Antaryatra account (upon request)</li>
            </ul>
          </section>

          <section>
            <h2>7. Processing Time</h2>
            <p>Refund processing times vary by payment method:</p>
            <ul>
              <li><strong>Credit/Debit Cards:</strong> 5-10 business days</li>
              <li><strong>Digital Wallets:</strong> 2-5 business days</li>
              <li><strong>Bank Transfer:</strong> 3-7 business days</li>
              <li><strong>Platform Credits:</strong> Immediate upon approval</li>
            </ul>
          </section>

          <section>
            <h2>8. Partial Refunds</h2>
            <p>
              In certain situations, partial refunds may be offered as compensation for:
            </p>
            <ul>
              <li>Service quality issues that don't warrant a full refund</li>
              <li>Minor deviations from service descriptions</li>
              <li>Delayed service delivery</li>
              <li>Inconvenience caused by service provider issues</li>
            </ul>
          </section>

          <section>
            <h2>9. Service Provider Refunds</h2>
            <p>
              When service providers initiate cancellations:
            </p>
            <ul>
              <li>Full refund will be processed automatically</li>
              <li>Additional compensation may be provided for inconvenience</li>
              <li>Priority rebooking assistance will be offered</li>
              <li>Service provider may be subject to penalties</li>
            </ul>
          </section>

          <section>
            <h2>10. Dispute Resolution</h2>
            <p>
              If you disagree with our refund decision:
            </p>
            <ul>
              <li>You may appeal the decision within 7 days</li>
              <li>Provide additional evidence or documentation</li>
              <li>Our senior team will conduct a secondary review</li>
              <li>Final decisions will be communicated within 5 business days</li>
            </ul>
          </section>

          <section>
            <h2>11. Platform Fees</h2>
            <p>
              Platform convenience fees are generally non-refundable unless:
            </p>
            <ul>
              <li>The cancellation is due to service provider unavailability</li>
              <li>Technical issues on our platform caused the cancellation</li>
              <li>Fraudulent activity is detected</li>
            </ul>
          </section>

          <section>
            <h2>12. Force Majeure</h2>
            <p>
              In cases of natural disasters, government restrictions, or other force majeure events, 
              special refund policies may apply. We will communicate any temporary policy changes 
              through our platform and direct communications.
            </p>
          </section>

          <section>
            <h2>13. Contact for Refunds</h2>
            <p>
              For refund requests and inquiries, please contact us at:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> communication@antaryatra.org</p>
              <p><strong>Phone:</strong> +91 92175 88609</p>
              <p><strong>Support Hours:</strong> Monday to Friday, 9:00 AM to 6:00 PM</p>
              {/* <p><strong>Emergency Contact:</strong> Available 24/7 for urgent issues</p> */}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;